import Drawer from '@/components/drawers'
import { validationMixin } from "vuelidate"
import { required, numeric } from "vuelidate/lib/validators"
import Loading from "@/components/widgets/Loading.vue"

export default {
  props: {
    opened: {
      type: Boolean,
      default: false
    },
    mode: {
      type: String
    },
    id: {
      type: String
    }
  },
  data() {
    return {
      loading: false,
      drawerOpened: false,
      sellProductList:[],
      categoryOptions: [], // หมวดสินค้า ERP (item_categories)
      unitOptions: [],     // master หน่วยนับกลาง
      form: {
        code: null,
        name: null,
        description: null,
        main_product_id: null,
        roll_multiplier: 1, // ตัวคูณลอน (งานโป๊ว) default 1
        production_multiplier: 1, // ตัวคูณการผลิต (เมตรม้วน/สินค้า 1 ม.) ใช้ตัดม้วนตอนออกใบผลิตโป๊ว
        category_id: null,  // หมวดสินค้า — ผูกที่ SKU สต็อกทุกสาขา inherit
        unit_id: null,      // หน่วยนับ (หน่วยเล็กสุด) — ผูกที่ SKU เช่นกัน
      },
      // ตัวแปลงหน่วยใหญ่ต่อ SKU: [{unit_id, base_qty}] — 1 หน่วยใหญ่ = base_qty หน่วยเล็กสุด
      conversions: []
    }
  },
  components: {
    Loading,
    Drawer
  },
  watch: {
    'opened'(n, o) {
      this.onInitSellProductTypeList()
      this.onInitCategoryAndUnitOptions()

      if (n) {
        this.showFileInput = true
        this.drawerOpened = n
        if (this.mode === 'update') {
          this.onInitData()
        }
      }
    }
  },
  computed: {
    // หน่วยที่เลือกเป็น "หน่วยใหญ่" ได้ = ทุกหน่วยยกเว้นหน่วยเล็กสุดของ SKU เอง
    bigUnitOptions() {
      return this.unitOptions.filter(u => u.id !== this.form.unit_id)
    },
    baseUnitName() {
      const u = this.unitOptions.find(x => x.id === this.form.unit_id)
      return u ? u.name : 'หน่วยเล็กสุด'
    },
  },
  mixins: [validationMixin],
  validations: {
    form: {
      code: { required },
      name: { required },
      main_product_id: { required },
    },
  },
  methods: {
    resetForm () {
      this.form.code = null
      this.form.name = null
      this.form.main_product_id = null
      this.form.description = null
      this.form.roll_multiplier = 1
      this.form.production_multiplier = 1
      this.form.category_id = null
      this.form.unit_id = null
      this.conversions = []

      this.$nextTick(() => {
        this.$v.$reset()
      })
    },
    formBuilder(obj) {
      this.form.code = obj.data?.code ?? ''
      this.form.name = obj.data?.name ?? ''
      this.form.main_product_id = obj.data?.main_product_id ?? null
      this.form.description = obj.data?.description ?? ''
      this.form.roll_multiplier = obj.data?.roll_multiplier ?? 1
      this.form.production_multiplier = obj.data?.production_multiplier ?? 1
      this.form.category_id = obj.data?.category_id ?? null
      this.form.unit_id = obj.data?.unit_id ?? null
      this.conversions = (obj.data?.unit_conversions ?? []).map(c => ({
        unit_id: c.unit_id, base_qty: parseFloat(c.base_qty)
      }))

      this.$v.form.$touch()
    },
    onCloseDrawer(event) {
      this.drawerOpened = false
      this.resetForm()
      this.$emit('close-event', { 
        status: true
      })
    },
    async onInitData() {
      try {
        const result = await this.$axios.get(
          `${this.$host}/api/v1/sku/${this.id}`
        )
        if (result.status === 200) {
          this.formBuilder(result)
        }
      } catch (error) {
        this.onExceptionHandler((error.response && error.response.data && error.response.data.message) || 'เกิดข้อผิดพลาด กรุณาลองใหม่อีกครั้ง');
      }
    },
    async onInitCategoryAndUnitOptions() {
      if (this.categoryOptions.length && this.unitOptions.length) return
      try {
        const [cats, units] = await Promise.all([
          this.$axios.get(`${this.$host}/api/v1/miscellaneous/categories`),
          this.$axios.get(`${this.$host}/api/v1/units`),
        ])
        this.categoryOptions = cats.data || []
        this.unitOptions = units.data || []
      } catch (error) {
        this.categoryOptions = []
        this.unitOptions = []
      }
    },
    async onInitSellProductTypeList() {
      try {
        const result = await this.$axios.get(
          `${this.$host}/api/v1/all-product-types`
        )
        if (result.status === 200) {
          const data = result.data.data
          this.sellProductList = data
        }
      } catch (error) {
        this.onExceptionHandler((error.response && error.response.data && error.response.data.message) || 'เกิดข้อผิดพลาด กรุณาลองใหม่อีกครั้ง');
      }
    },
    async onSubmitForm() {
      try {
        this.$v.form.$touch()
        if (this.$v.form.$anyError) {
          return
        }
        this.loading = true
        const formData = new FormData()
        formData.append('code', this.form.code?? '')
        formData.append('name', this.form.name?? '')
        formData.append('main_product_id', this.form.main_product_id?? '')
        formData.append('description', this.form.description?? '')
        formData.append('roll_multiplier', (this.form.roll_multiplier === null || this.form.roll_multiplier === '' ? 1 : this.form.roll_multiplier))
        formData.append('production_multiplier', (this.form.production_multiplier === null || this.form.production_multiplier === '' ? 1 : this.form.production_multiplier))
        formData.append('category_id', this.form.category_id ?? '')
        formData.append('unit_id', this.form.unit_id ?? '')
        // ตัวแปลงหน่วย: ส่งเป็นชุดสมบูรณ์เสมอ (backend ลบของเก่าแล้วลงใหม่ + กรองแถวขยะเอง)
        formData.append('conversions', JSON.stringify(
          this.conversions.filter(c => c.unit_id && parseFloat(c.base_qty) > 0)
        ))

        let result = await this.$axios.post(this.$host + "/api/v1/sku" + (this.mode === 'update' ? '/' + this.id  : ''), formData)

        this.loading = false   
        setTimeout(() => {
          if (result.status === 201 || result.status === 200) {
            this.$swal({
              width: "29rem",
              padding: '1.825em',
              html: '<div class="d-flex flex-row align-items-center">' +
                '<i class="fa fa-2x mr-3 fa-exclamation-triangle text-info"></i>'+
                '<div class="d-flex flex-column">'+
                '<span class="font-bold text-base py-2.5 text-left">แจ้งเตือน</span>'+
                `<span class="text-base text-left text-gray-500">${result.data?.message ?? "บันทึกข้อมูลเรียบร้อยแล้ว"}</span>`+
                '</div>'+
              '</div>',
                allowOutsideClick: false,
                focusConfirm: false,
                customClass: {
                  popup: 'swal2-custom-rounded',
                  closeButton: 'text-3xl swal2-custom-close',
                  actions: 'justify-content-end',
                  contant: 'd-flex flex-row justify-content-around px-2',
                  title: 'd-none',
                  confirmButton: "btn btn-info py-3 rounded-lg text-white font-prompt-light text-sm font-normal",
                  cancelButton: 'btn btn-google py-3 rounded-lg text-white font-prompt-light text-sm font-normal'
                },
                showCancelButton: false,
                confirmButtonText: "ตกลง",
                cancelButtonText: "ยกเลิก",
                showCloseButton: true,
            }).then(result => {
              this.loading = false
              this.onCloseDrawer()
            })
          }
        }, 500)
      } catch (error) {
        this.loading = false
        this.onExceptionHandler((error.response && error.response.data && error.response.data.message) || 'เกิดข้อผิดพลาด กรุณาลองใหม่อีกครั้ง');
      }
    },
  }
}
